import { getAuth } from "firebase/auth";
import { OptimizedSessionAuthService } from "./optimizedSessionAuth.service";
import {
  safeConsoleError,
  safeConsoleLog,
  safeConsoleWarn,
} from "../utils/errorSanitizer";
import { detectMobile } from "../utils/mobileDetection";

export interface TransferResult {
  success: boolean;
  error?: string;
  redirectPath?: string;
}

export class AppTransferService {
  /**
   * Transfer from current app to target app
   * Uses session cookies for cross-subdomain auth
   */
  static async transferToApp(
    targetApp: "main" | "whitelabel",
    targetPath: string = "/communities",
    subdomain?: string
  ): Promise<TransferResult> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return {
          success: false,
          error: "User not authenticated",
        };
      }

      return this.productionTransfer(targetApp, targetPath, subdomain);
    } catch (error: any) {
      safeConsoleError("Transfer failed:", error);
      return {
        success: false,
        error: error.message || "Transfer failed",
      };
    }
  }

  /**
   * Production transfer using session cookies
   */
  private static async productionTransfer(
    targetApp: "main" | "whitelabel",
    targetPath: string,
    subdomain?: string
  ): Promise<TransferResult> {
    safeConsoleLog(
      `Production transfer to ${targetApp}${subdomain ? ` (${subdomain})` : ""}`
    );

    // CRITICAL: Ensure session is created for cross-subdomain auth BEFORE redirecting
    try {
      const sessionResult = await OptimizedSessionAuthService.createSession();
      if (!sessionResult.success) {
        return {
          success: false,
          error: sessionResult.error || "Failed to create session for transfer",
        };
      }
    } catch (sessionError) {
      safeConsoleError("Failed to create session:", sessionError);
      return {
        success: false,
        error: "Failed to create session for cross-subdomain transfer",
      };
    }

    // Determine target URL
    const targetUrl = this.getTargetAppUrl(targetApp, targetPath, subdomain);

    // Set transfer flag for the receiving app to detect
    sessionStorage.setItem("recent_transfer", Date.now().toString());

    // Clear service worker context before cross-domain navigation to prevent asset conflicts
    try {
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        // Unregister current service worker to prevent cross-domain interference
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
    } catch (swError) {
      // Continue with transfer even if SW cleanup fails
    }

    // Check if we're in PWA mode and handle navigation accordingly
    const isPWA = this.isPWAMode();

    if (isPWA) {
      // Google's recommendation: Use redirect URL strategy for PWA cross-domain navigation
      safeConsoleLog("PWA mode detected, using Google's redirect strategy");

      // Import PWA redirect handler
      const { PWARedirectHandler } = await import("../utils/pwaRedirect");

      try {
        // Generate optimized redirect URL
        const targetHost = new URL(targetUrl).hostname;
        const targetPath =
          new URL(targetUrl).pathname + new URL(targetUrl).search;

        // Same domain - direct navigation
        if (targetHost === window.location.hostname) {
          window.history.pushState(null, "", targetPath);
          window.dispatchEvent(new PopStateEvent("popstate"));
          return { success: true, redirectPath: targetPath };
        }

        // Cross-domain - use redirect URL strategy
        const redirectUrl = PWARedirectHandler.generateRedirectUrl(
          targetHost,
          targetPath
        );
        safeConsoleLog(`PWA cross-domain redirect: ${redirectUrl}`);
        window.location.href = redirectUrl;
      } catch (pwaError) {
        safeConsoleLog(
          "PWA redirect failed, falling back to direct navigation",
          pwaError
        );
        window.location.href = targetUrl;
      }
    } else {
      // For browser mode, use window.location.replace for a clean navigation
      safeConsoleLog(
        "Browser mode detected, using location.replace for navigation"
      );
      window.location.replace(targetUrl);
    }

    return {
      success: true,
      redirectPath: targetUrl,
    };
  }

  /**
   * Handle incoming transfer from another app
   * Uses session-based authentication
   */
  static async handleIncomingTransfer(): Promise<TransferResult> {
    try {
      safeConsoleLog("Checking for incoming transfer...");
      return this.handleProductionTransfer();
    } catch (error: any) {
      safeConsoleError("Error handling incoming transfer:", error);
      return {
        success: false,
        error: error.message || "Failed to handle transfer",
      };
    }
  }

  /**
   * Handle production transfer using session cookies
   */
  private static async handleProductionTransfer(): Promise<TransferResult> {
    const auth = getAuth();

    // Give more time for PWA auth context to stabilize, especially on mobile
    const mobileDetection = detectMobile();
    const delayTime = mobileDetection.isMobile ? 2000 : 1500; // Longer delay for mobile PWAs
    await new Promise((resolve) => setTimeout(resolve, delayTime));

    if (auth.currentUser) {
      safeConsoleLog(
        "Production: Transfer successful - user authenticated via session"
      );
      return {
        success: true,
        redirectPath: window.location.pathname,
      };
    } else {
      // Try to check auth status explicitly (throttled)
      try {
        const sessionResult =
          await OptimizedSessionAuthService.checkAuthStatus();
        if (sessionResult.success) {
          safeConsoleLog(
            "Production: Transfer successful - authenticated via session check"
          );
          return {
            success: true,
            redirectPath: window.location.pathname,
          };
        }
      } catch (error) {
        safeConsoleWarn("Session auth check failed:", error);
      }
    }

    return {
      success: false,
      error: "No valid session found",
    };
  }

  /**
   * Check if current page load is a transfer
   * Detects if user recently navigated from another domain (indicating transfer)
   */
  static isTransferPage(): boolean {
    // Check if this is a recent cross-origin navigation (PWA transfer)
    const now = Date.now();
    const navigationTime = now - performance.timeOrigin;

    // If the page loaded very recently (< 5 seconds), check for transfer indicators
    if (navigationTime < 5000) {
      // Check referrer for cross-domain navigation
      const referrer = document.referrer;
      const currentDomain = window.location.hostname;

      if (referrer) {
        try {
          const referrerDomain = new URL(referrer).hostname;

          // Security: Strict validation of ZuriLink domains only
          const isDifferentZuriLinkDomain = this.isValidTransferDomain(
            referrerDomain,
            currentDomain
          );

          if (isDifferentZuriLinkDomain) {
            return true;
          }
        } catch (error) {
          // Invalid referrer URL, ignore
        }
      }

      // Check for transfer-related URL parameters or localStorage flags
      const url = new URL(window.location.href);
      if (
        url.searchParams.has("utm_source") &&
        url.searchParams.get("utm_source") === "pwa"
      ) {
        return true;
      }

      // Check for recent transfer flag in sessionStorage (with validation)
      const transferFlag = sessionStorage.getItem("recent_transfer");
      if (transferFlag) {
        // Security: Validate that transferFlag is a valid timestamp
        const transferTime = parseInt(transferFlag, 10);
        if (
          !isNaN(transferTime) &&
          transferTime > 0 &&
          now - transferTime < 10000
        ) {
          // 10 seconds
          // Clear flag immediately to prevent replay attacks
          sessionStorage.removeItem("recent_transfer");
          return true;
        } else {
          sessionStorage.removeItem("recent_transfer");
        }
      }
    }

    return false;
  }

  /**
   * Get the target app URL based on environment
   */
  private static getTargetAppUrl(
    targetApp: "main" | "whitelabel",
    targetPath: string,
    subdomain?: string
  ): string {
    const isLocalhost = window.location.hostname.includes("localhost");

    if (isLocalhost) {
      // Development environment
      if (targetApp === "main") {
        return `http://localhost:3000${targetPath}`;
      } else {
        // For whitelabel in dev, use subdomain.localhost:3001
        const subdomainPrefix = subdomain ? `${subdomain}.` : "";
        return `http://${subdomainPrefix}localhost:3001${targetPath}`;
      }
    } else {
      // Production environment
      const currentHostname = window.location.hostname;

      if (
        currentHostname.endsWith(".web.app") ||
        currentHostname.endsWith(".firebaseapp.com")
      ) {
        // Firebase hosting environment - no subdomain support, use separate hosting targets
        if (targetApp === "main") {
          return `https://zurilink-28b09.web.app${targetPath}`;
        } else {
          // For whitelabel, use the separate hosting target (no subdomain possible)
          // Note: Firebase hosting doesn't support dynamic subdomains, so all whitelabel traffic goes to one hosting target
          return `https://zurilink-28b09-c1549.web.app${targetPath}`;
        }
      } else {
        // Custom domain environment (zurilink.com) - use subdomains for session cookie sharing

        if (targetApp === "main") {
          // Main app uses root domain
          return `https://zurilink.com${targetPath}`;
        } else {
          // Whitelabel uses specific subdomain

          if (!subdomain) {
            safeConsoleError("No subdomain provided for whitelabel transfer!");
            throw new Error(
              "Subdomain is required for whitelabel app transfers"
            );
          }
          return `https://${subdomain}.zurilink.com${targetPath}`;
        }
      }
    }
  }

  /**
   * Transfer to main app
   */
  static async transferToMain(
    targetPath: string = "/communities"
  ): Promise<TransferResult> {
    return this.transferToApp("main", targetPath);
  }

  /**
   * Transfer to whitelabel app
   */
  static async transferToWhitelabel(
    targetPath: string = "/",
    subdomain?: string
  ): Promise<TransferResult> {
    // Validate subdomain is provided for production transfers
    const isLocalhost = window.location.hostname.includes("localhost");
    const isFirebaseHosting =
      window.location.hostname.endsWith(".web.app") ||
      window.location.hostname.endsWith(".firebaseapp.com");

    // Only require subdomain for custom domain (production) transfers
    if (!isLocalhost && !isFirebaseHosting && !subdomain) {
      return {
        success: false,
        error:
          "Subdomain is required for whitelabel app transfers in production",
      };
    }

    return this.transferToApp("whitelabel", targetPath, subdomain);
  }

  /**
   * Navigate to whitelabel from main app (compatibility method for CrossAppTransferService)
   */
  static async navigateToWhitelabel(
    _user: any,
    affiliationSubdomain: string,
    targetPath = "/communities",
    _affiliationId?: string
  ): Promise<void> {
    safeConsoleLog(
      "navigateToWhitelabel called with subdomain:",
      affiliationSubdomain
    );

    // Validate that subdomain is provided
    if (!affiliationSubdomain || affiliationSubdomain.trim() === "") {
      throw new Error(
        "Affiliation subdomain is required for whitelabel navigation"
      );
    }

    // Use the provided subdomain for the transfer
    const result = await this.transferToWhitelabel(
      targetPath,
      affiliationSubdomain
    );

    if (!result.success) {
      throw new Error(result.error || "Transfer failed");
    }
  }

  /**
   * Navigate to main app from whitelabel (compatibility method for CrossAppTransferService)
   */
  static async navigateToMainApp(
    _user: any,
    targetPath = "/"
  ): Promise<void> {
    // Ignore user parameter - use simple transfer
    const result = await this.transferToMain(targetPath);

    if (!result.success) {
      throw new Error(result.error || "Transfer failed");
    }
  }

  /**
   * Detect if the app is running in PWA mode with enhanced mobile detection
   */
  private static isPWAMode(): boolean {
    const mobileDetection = detectMobile();

    // Priority 1: Capacitor (native app wrapper)
    if (mobileDetection.isCapacitor) {
      return true;
    }

    // Priority 2: iOS PWA detection (most reliable for iOS)
    if (mobileDetection.isIOSPWA) {
      return true;
    }

    // Priority 3: iOS standalone mode (iOS Safari PWA)
    if (mobileDetection.isIOS && (navigator as any).standalone === true) {
      return true;
    }

    // Priority 4: Standard PWA display mode (Android Chrome, Desktop)
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }

    // Priority 5: Other PWA display modes
    if (
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches
    ) {
      return true;
    }

    // Priority 6: Additional mobile PWA indicators
    // Only use the PWA start_url marker as a reliable heuristic
    if (mobileDetection.isMobile) {
      const url = new URL(window.location.href);
      if (url.searchParams.get("source") === "pwa") {
        return true;
      }
    }

    return false;
  }

  /**
   * Validate ZuriLink domains for security
   */
  private static isSecureZurilinkDomain(hostname: string): boolean {
    // Localhost validation
    if (hostname.includes("localhost")) {
      return /^localhost(:\d+)?$/.test(hostname);
    }

    // Main domain
    if (hostname === "zurilink.com") {
      return true;
    }

    // Subdomain validation
    if (hostname.endsWith(".zurilink.com")) {
      const subdomain = hostname.replace(".zurilink.com", "");
      // Strict subdomain validation: alphanumeric + hyphens, 1-63 chars
      return /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(subdomain);
    }

    // Firebase hosting (for staging/dev)
    if (
      hostname.endsWith(".web.app") ||
      hostname.endsWith(".firebaseapp.com")
    ) {
      return /^[a-zA-Z0-9-]+\.(web\.app|firebaseapp\.com)$/.test(hostname);
    }

    return false;
  }

  /**
   * Validate if transfer between domains is legitimate and secure
   */
  private static isValidTransferDomain(
    referrerDomain: string,
    currentDomain: string
  ): boolean {
    // Both domains must be valid ZuriLink domains
    if (
      !this.isSecureZurilinkDomain(referrerDomain) ||
      !this.isSecureZurilinkDomain(currentDomain)
    ) {
      return false;
    }

    // Domains must be different to constitute a transfer
    if (referrerDomain === currentDomain) {
      return false;
    }

    // For localhost, allow cross-port transfers
    if (
      referrerDomain.includes("localhost") &&
      currentDomain.includes("localhost")
    ) {
      return true;
    }

    // For production, allow transfers between main domain and subdomains
    const isReferrerMain = referrerDomain === "zurilink.com";
    const isReferrerSubdomain = referrerDomain.endsWith(".zurilink.com");
    const isCurrentMain = currentDomain === "zurilink.com";
    const isCurrentSubdomain = currentDomain.endsWith(".zurilink.com");

    return (
      (isReferrerMain || isReferrerSubdomain) &&
      (isCurrentMain || isCurrentSubdomain)
    );
  }
}
